import { cn } from "@/lib/utils";
import { useNavigate, useLocation } from "react-router-dom";
import { Activity, Brain, Zap, CheckCircle2, ChevronRight } from "lucide-react";

interface SIARFlowIndicatorProps {
  currentStep: 'signals' | 'insights' | 'actions' | 'results';
  context?: {
    signalCount?: number;
    insightCount?: number;
    actionCount?: number;
    resultCount?: number;
  };
}

const steps = [
  { key: 'signals', label: 'Signals', icon: Activity, path: '/admin', description: 'Measure' },
  { key: 'insights', label: 'Insights', icon: Brain, path: '/admin/safety-intelligence', description: 'Diagnose' },
  { key: 'actions', label: 'Actions', icon: Zap, path: '/admin/action-queue', description: 'Act' },
  { key: 'results', label: 'Results', icon: CheckCircle2, path: '/admin/results', description: 'Verify' },
] as const;

export function SIARFlowIndicator({ currentStep, context }: SIARFlowIndicatorProps) {
  const navigate = useNavigate();
  const currentIndex = steps.findIndex(s => s.key === currentStep);

  const getCount = (key: string) => {
    if (!context) return null;
    switch (key) {
      case 'signals': return context.signalCount;
      case 'insights': return context.insightCount;
      case 'actions': return context.actionCount;
      case 'results': return context.resultCount;
      default: return null;
    }
  };

  return (
    <div className="flex items-center gap-1 p-3 rounded-lg bg-muted/30 border overflow-x-auto">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isActive = step.key === currentStep;
        const isPast = index < currentIndex;
        const isFuture = index > currentIndex;
        const count = getCount(step.key);

        return (
          <div key={step.key} className="flex items-center">
            <button
              onClick={() => navigate(step.path)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-md transition-all",
                isActive && "bg-primary text-primary-foreground shadow-sm",
                isPast && "text-muted-foreground hover:bg-muted hover:text-foreground",
                isFuture && "text-muted-foreground/50 hover:bg-muted/50 hover:text-muted-foreground"
              )}
            >
              <Icon className={cn("h-4 w-4", isActive && "animate-pulse")} />
              <div className="text-left">
                <span className={cn("text-sm font-medium", !isActive && "text-xs")}>
                  {step.label}
                </span>
                {isActive && (
                  <span className="block text-[10px] opacity-80">{step.description}</span>
                )}
              </div>
              {count !== null && count > 0 && (
                <span className={cn(
                  "text-xs px-1.5 py-0.5 rounded-full",
                  isActive ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"
                )}>
                  {count}
                </span>
              )}
            </button>
            {index < steps.length - 1 && (
              <ChevronRight className={cn(
                "h-4 w-4 mx-1 shrink-0",
                index < currentIndex ? "text-primary" : "text-muted-foreground/30"
              )} />
            )}
          </div>
        );
      })}
    </div>
  );
}
